import { Capacitor } from "@capacitor/core";
import { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { AuthProvider } from "./AuthContext";
import { AppShell } from "./components/AppShell";
import { ActivatedRoute, ProtectedRoute } from "./components/ProtectedRoute";
import { ActivatePage } from "./pages/ActivatePage";
import { AICopilotPage } from "./pages/AICopilotPage";
import { HomePage } from "./pages/HomePage";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage";
import { GuidePage } from "./pages/GuidePage";
import { LoginPage } from "./pages/LoginPage";
import { ProfilePage } from "./pages/ProfilePage";
import { RegisterPage } from "./pages/RegisterPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { TranslatePage } from "./pages/TranslatePage";
import { TravelPage } from "./pages/TravelPage";
import { WelcomePage } from "./pages/WelcomePage";
import { PrivacyPage, TermsPage } from "./pages/LegalPages";
import { NativeSpeech } from "./lib/nativeSpeech";

function VoiceCommandRouter() {
  const navigate = useNavigate();
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let disposed = false;
    let removeListener: (() => Promise<void>) | undefined;
    const openTranslator = (command?: string | null) => {
      if (command === "start") navigate("/app/translate?autostart=1");
    };
    void NativeSpeech.addListener("voiceCommand", ({ command }) => openTranslator(command)).then((handle) => {
      if (disposed) void handle.remove();
      else removeListener = handle.remove;
    });
    void NativeSpeech.consumePendingCommand().then(({ command }) => openTranslator(command)).catch(() => undefined);
    return () => {
      disposed = true;
      if (removeListener) void removeListener();
    };
  }, [navigate]);
  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <VoiceCommandRouter />
        <Routes>
          <Route path="/" element={<WelcomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route element={<ProtectedRoute />}>
            <Route path="/activate" element={<ActivatePage />} />
            <Route element={<ActivatedRoute />}>
              <Route path="/app" element={<AppShell />}>
                <Route index element={<HomePage />} />
                <Route path="ai" element={<AICopilotPage />} />
                <Route path="translate" element={<TranslatePage />} />
                <Route path="travel" element={<TravelPage />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="guide" element={<GuidePage />} />
              </Route>
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
